// @ts-nocheck
// components/CoursesCatalog.tsx — FIXED VERSION v4.3
// =====================================================================
// NEW IN v4.3:
//   ✅ Real “topic filter” via initialTopic prop (URL ?topic=...)
//   ✅ Shows "מסונן לפי תחום" pill with X to clear
//   ✅ Smart matching: exact if exists, else includes
// =====================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Image,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { Search, X, ChevronDown, Tag, ExternalLink, ListFilter as Filter } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { trackButtonClick } from '@/utils/analytics';
import { trackCourseClick } from '@/utils/leadCourseSelectionService';

// ─── Constants ────────────────────────────────────────────────────────────────

const KURSI_PURPLE = '#B026FF';
const TEXT = '#0B1020';
const TEXT_MUTED = '#4B5563';
const BG_CARD = 'rgba(255,255,255,0.95)';
const BORDER = 'rgba(15,23,42,0.08)';
const PAGE_SIZE = 12;

const EMPTY_VALUES = new Set([
  '',
  'לא צוין',
  'לא ידוע',
  'n/a',
  'N/A',
  'null',
  'undefined',
  '-',
  '—',
]);

const normalizeStr = (v?: string | null): string => (v ?? '').toString().trim();

const isValidValue = (v?: string | null): boolean => {
  const s = normalizeStr(v);
  return !!s && !EMPTY_VALUES.has(s);
};

const normalizeKey = (v?: string | null): string =>
  normalizeStr(v)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Course {
  id: number;
  course_id: string;
  source_url_key: string;
  title: string;
  provider: string;
  topic: string;
  type: string;
  level: string;
  format: string;
  price: string;
  duration: string;
  language: string;
  summary: string;
  images: string;
  source_urls: string;
  voucher_text: string;
  has_voucher: boolean;
  audience: string;
  outcomes: string;
}

export type SchemaCourseLite = Pick<Course, 'title' | 'provider' | 'source_urls'>;

interface ActiveFilters {
  type: string;
  provider: string;
  level: string;
  has_voucher: boolean | null;
}

const DEFAULT_FILTERS: ActiveFilters = {
  type: '',
  provider: '',
  level: '',
  has_voucher: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getFirstImageUrl = (raw?: string | null): string | null => {
  const s = normalizeStr(raw);
  if (!s) return null;
  return s.split(',').map((p) => p.trim()).filter(Boolean)[0] ?? null;
};

const isNextCollegeProvider = (provider: string): boolean =>
  (provider || '').toLowerCase().replace(/[\s-]/g, '').includes('nextcollege');

interface TopicTheme {
  emoji: string;
  from: string;
  to: string;
  image: string;
}

const TOPIC_THEMES: Array<{ keys: string[]; theme: TopicTheme }> = [
  { keys: ['בינה מלאכותית', 'ai', 'llm', 'chatgpt', "ג'נרטיב"], theme: { emoji: '🤖', from: '#6366F1', to: '#A855F7', image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['סייבר', 'cyber', 'אבטחה', 'security', 'hacking', 'ethical hack'], theme: { emoji: '🔒', from: '#EF4444', to: '#B026FF', image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['פיתוח תוכנה', 'programming', 'קוד', 'coding', 'python', 'javascript', 'java', 'web dev', 'פרונט', 'בקאנד', 'fullstack', 'מפתח'], theme: { emoji: '💻', from: '#0EA5E9', to: '#6366F1', image: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['web', 'אתר', 'אינטרנט', 'html', 'css', 'react', 'node'], theme: { emoji: '🌐', from: '#06B6D4', to: '#0EA5E9', image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['שיווק דיגיטלי', 'דיגיטל מרקטינג', 'digital marketing', 'מרקטינג', 'advertising', 'ads', 'פרסום', 'meta ads', 'google ads', 'performance'], theme: { emoji: '📢', from: '#10B981', to: '#0EA5E9', image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['שוק ההון', 'בורסה', 'מניות', 'השקעות', 'stock', 'trading', 'קריפטו', 'crypto', 'פיננסים', 'finance', 'forex'], theme: { emoji: '📊', from: '#F59E0B', to: '#EF4444', image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['פסיכומטרי', 'psychometric', 'מתמטיקה', 'אנגלית', 'יחידה', 'קצב'], theme: { emoji: '🧠', from: '#8B5CF6', to: '#EC4899', image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['ui', 'ux', 'עיצוב', 'design', 'figma', 'גרפי', 'product design'], theme: { emoji: '🎨', from: '#EC4899', to: '#F97316', image: 'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['data', 'נתונים', 'analytics', 'bi ', 'tableau', 'power bi', 'sql', 'excel', 'מדעי הנתונים'], theme: { emoji: '📈', from: '#06B6D4', to: '#6366F1', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['seo', 'קידום אתרים', 'קידום אורגני', 'content', 'תוכן'], theme: { emoji: '🔍', from: '#84CC16', to: '#0EA5E9', image: 'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['כושר', 'fitness', 'ספורט', 'תזונה', 'בריאות', 'health', 'yoga', 'פילאטיס'], theme: { emoji: '💪', from: '#F97316', to: '#EF4444', image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['נהיגה', 'driving', 'רכב', 'תנועה'], theme: { emoji: '🚗', from: '#6B7280', to: '#374151', image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['צילום', 'photo', 'וידאו', 'video', 'עריכה', 'adobe', 'premiere', 'lightroom'], theme: { emoji: '📸', from: '#7C3AED', to: '#EC4899', image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['מוזיקה', 'music', 'גיטרה', 'פסנתר', 'שיר'], theme: { emoji: '🎵', from: '#8B5CF6', to: '#6366F1', image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['עסקים', 'business', 'יזמות', 'startup', 'ניהול', 'management', 'פרויקט', 'agile', 'scrum'], theme: { emoji: '💼', from: '#1E40AF', to: '#3B82F6', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['משפטי', 'law', 'חשבונאות', 'כספים', 'מס', 'ביטוח'], theme: { emoji: '⚖️', from: '#374151', to: '#1E40AF', image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['שפה', 'language', 'ספרדית', 'צרפתית', 'אנגלית', 'עברית', 'יידיש', 'ערבית'], theme: { emoji: '🗣️', from: '#0D9488', to: '#0EA5E9', image: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['real estate', 'נדלן', 'נדל"ן', 'מקרקעין'], theme: { emoji: '🏠', from: '#92400E', to: '#F59E0B', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=70' } },
  { keys: ['chef', 'בישול', 'אוכל', 'קונדיטוריה', 'מזון'], theme: { emoji: '🍳', from: '#B45309', to: '#F97316', image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=600&q=70' } },
];

const DEFAULT_TOPIC_THEME: TopicTheme = { emoji: '📚', from: '#B026FF', to: '#6366F1', image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=600&q=70' };

function getTopicTheme(topic: string): TopicTheme {
  const key = (topic || '').toLowerCase();
  for (const { keys, theme: t } of TOPIC_THEMES) {
    if (keys.some((k) => key.includes(k.toLowerCase()))) return t;
  }
  return DEFAULT_TOPIC_THEME;
}



const getFirstUrl = (raw?: string | null): string | null => {
  const s = normalizeStr(raw);
  if (!s) return null;
  return s.split(',').map((p) => p.trim()).filter(Boolean)[0] ?? null;
};

const dedupeCourses = (rows: Course[]): Course[] => {
  const map = new Map<string, Course>();

  for (const r of rows) {
    const sourceUrlKey = normalizeStr(r.source_url_key);
    const courseId = normalizeStr(r.course_id);
    const fallbackKey = [
      normalizeStr(r.provider).toLowerCase(),
      normalizeStr(r.title).toLowerCase(),
      (getFirstUrl(r.source_urls) ?? '').toLowerCase(),
    ].join('||');

    const key = sourceUrlKey ? `suk:${sourceUrlKey}` : courseId ? `cid:${courseId}` : `fb:${fallbackKey}`;

    const existing = map.get(key);
    if (!existing) {
      map.set(key, r);
      continue;
    }
    if ((r.id ?? 0) > (existing.id ?? 0)) {
      map.set(key, r);
    }
  }

  return Array.from(map.values()).sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
};

const normalizeCourseRow = (c: any): Course => ({
  ...c,
  course_id: normalizeStr(c.course_id),
  source_url_key: normalizeStr(c.source_url_key),
  title: normalizeStr(c.title),
  provider: normalizeStr(c.provider),
  topic: normalizeStr(c.topic),
  type: normalizeStr(c.type),
  level: normalizeStr(c.level),
  format: normalizeStr(c.format),
  price: normalizeStr(c.price),
  duration: normalizeStr(c.duration),
  language: normalizeStr(c.language),
  summary: normalizeStr(c.summary),
  images: normalizeStr(c.images),
  source_urls: normalizeStr(c.source_urls),
  voucher_text: normalizeStr(c.voucher_text),
  audience: normalizeStr(c.audience),
  outcomes: normalizeStr(c.outcomes),
  has_voucher: !!c.has_voucher,
});

// ─── FilterChip ───────────────────────────────────────────────────────────────

const FilterChip = ({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) => (
  <Pressable
    style={[chipStyles.chip, active && chipStyles.chipActive]}
    onPress={onPress}
    accessible
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ selected: active }}
  >
    <Text style={[chipStyles.chipText, active && chipStyles.chipTextActive]}>{label}</Text>
  </Pressable>
);

const chipStyles = StyleSheet.create({
  chip: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: BG_CARD,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 4px 12px rgba(15,23,42,0.06)' } as any) : {}),
  },
  chipActive: {
    borderColor: KURSI_PURPLE,
    backgroundColor: `${KURSI_PURPLE}14`,
  },
  chipText: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: TEXT_MUTED,
    textAlign: 'center',
  },
  chipTextActive: {
    fontFamily: theme.fonts.bold,
    color: KURSI_PURPLE,
  },
});

// ─── CourseCard ───────────────────────────────────────────────────────────────

const CourseCard = ({
  course,
  onPress,
  cardWidth,
}: {
  course: Course;
  onPress: (c: Course) => void;
  cardWidth: number;
}) => {
  const metaItems = [course.type, course.level, course.format].filter(isValidValue) as string[];
  const showPrice = isValidValue(course.price);
  const showDuration = isValidValue(course.duration);
  const showPriceRow = showPrice || showDuration;

  const showRealImage = isNextCollegeProvider(course.provider);
  const nextCollegeImageUri = showRealImage ? getFirstImageUrl(course.images) : null;
  const topicTheme = showRealImage ? DEFAULT_TOPIC_THEME : getTopicTheme(course.topic);

  return (
    <Pressable
      style={({ pressed }) => [
        cardStyles.card,
        Platform.OS !== 'web' && { width: cardWidth },
        pressed && cardStyles.cardPressed,
      ]}
      onPress={() => onPress(course)}
      accessible
      accessibilityRole="button"
      accessibilityLabel={`קורס: ${course.title || 'ללא שם'}`}
      accessibilityHint={course.has_voucher ? 'לחץ כדי להשאיר פרטים ולקבל את ההטבה' : 'לחץ כדי לפתוח את הקורס'}
    >
      <View style={cardStyles.imageWrap}>
        {showRealImage && nextCollegeImageUri ? (
          <Image
            source={{ uri: nextCollegeImageUri }}
            style={cardStyles.image}
            resizeMode="cover"
            accessible
            accessibilityLabel={`תמונה של ${course.title}`}
          />
        ) : (
          <View
            style={cardStyles.topicIllustration}
            accessible
            accessibilityLabel={`נושא: ${course.topic || 'קורס'}`}
          >
            <Image
              source={{ uri: topicTheme.image }}
              style={StyleSheet.absoluteFillObject as any}
              resizeMode="cover"
            />
            <View style={cardStyles.topicImageOverlay} />
            {isValidValue(course.topic) && (
              <Text style={cardStyles.topicLabel} numberOfLines={1}>
                {course.topic}
              </Text>
            )}
          </View>
        )}

        {course.has_voucher && (
          <View style={cardStyles.voucherBadge}>
            <Tag size={10} color={KURSI_PURPLE} />
            <Text style={cardStyles.voucherBadgeText} numberOfLines={1}>
              {isValidValue(course.voucher_text) ? course.voucher_text : 'הטבה'}
            </Text>
          </View>
        )}
      </View>

      <View style={cardStyles.body}>
        <Text style={cardStyles.title} numberOfLines={2}>
          {course.title}
        </Text>

        {isValidValue(course.provider) && (
          <Text style={cardStyles.provider} numberOfLines={1}>
            {course.provider}
          </Text>
        )}

        {metaItems.length > 0 && (
          <View style={cardStyles.metaRow}>
            {metaItems.map((item, idx) => (
              <View key={`${item}-${idx}`} style={cardStyles.metaPill}>
                <Text style={cardStyles.metaPillText}>{item}</Text>
              </View>
            ))}
          </View>
        )}

        {showPriceRow && (
          <View style={cardStyles.priceRow}>
            {showPrice && (
              <Text style={cardStyles.price} numberOfLines={1}>
                {course.price}
              </Text>
            )}
            {showDuration && (
              <Text style={cardStyles.duration} numberOfLines={1}>
                ⏱ {course.duration}
              </Text>
            )}
          </View>
        )}

        {!!course.summary && (
          <Text style={cardStyles.summary} numberOfLines={3}>
            {course.summary}
          </Text>
        )}

        <View style={cardStyles.ctaRow}>
          <View style={[cardStyles.ctaButton, course.has_voucher && cardStyles.ctaButtonVoucher]}>
            {course.has_voucher ? (
              <>
                <Tag size={12} color="#FFF" />
                <Text style={cardStyles.ctaButtonText}>קבל הטבה</Text>
              </>
            ) : (
              <>
                <ExternalLink size={12} color={KURSI_PURPLE} />
                <Text style={[cardStyles.ctaButtonText, cardStyles.ctaButtonTextOutline]}>פרטים + הטבה</Text>
              </>
            )}
          </View>
        </View>
      </View>
    </Pressable>
  );
};

const cardStyles = StyleSheet.create({
  card: {
    width: Platform.OS === 'web' ? 290 : undefined,
    minWidth: Platform.OS === 'web' ? 270 : undefined,
    backgroundColor: BG_CARD,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0px 8px 28px rgba(15,23,42,0.09)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.08,
          shadowRadius: 10,
          elevation: 3,
        }),
  },
  cardPressed: {
    transform: [{ scale: 0.985 }],
    opacity: 0.95,
  },
  imageWrap: {
    width: '100%',
    height: Platform.OS === 'web' ? 150 : 130,
    maxHeight: Platform.OS === 'web' ? 150 : 130,
    backgroundColor: 'rgba(15,23,42,0.04)',
    position: 'relative',
    overflow: 'hidden',
  },
  image: { width: '100%', height: '100%' },
  topicIllustration: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    position: 'relative',
  },
  topicImageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(8,12,24,0.38)',
  },
  topicLabel: {
    fontFamily: theme.fonts.bold,
    fontSize: Platform.OS === 'web' ? 12 : 11,
    color: '#FFFFFF',
    textAlign: 'center',
    paddingHorizontal: 10,
    paddingBottom: 10,
    letterSpacing: 0.3,
    zIndex: 1,
    ...(Platform.OS === 'web'
      ? ({ textShadow: '0px 1px 6px rgba(0,0,0,0.60)' } as any)
      : {
          textShadowColor: 'rgba(0,0,0,0.60)',
          textShadowOffset: { width: 0, height: 1 },
          textShadowRadius: 4,
        }),
  },
  voucherBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255,255,255,0.97)',
    borderWidth: 1,
    borderColor: `${KURSI_PURPLE}33`,
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 4px 12px rgba(176,38,255,0.15)' } as any) : {}),
  } as any,
  voucherBadgeText: {
    fontFamily: theme.fonts.bold,
    fontSize: 11,
    color: KURSI_PURPLE,
    maxWidth: 120,
  },
  body: { padding: 14, gap: 6, flex: 1, alignItems: 'flex-end' },
  title: {
    fontFamily: theme.fonts.bold,
    fontSize: Platform.OS === 'web' ? 14 : 13,
    color: TEXT,
    textAlign: 'right',
    lineHeight: Platform.OS === 'web' ? 20 : 18,
    alignSelf: 'stretch',
  },
  provider: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: TEXT_MUTED,
    textAlign: 'right',
    alignSelf: 'stretch',
  },
  metaRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 4,
    alignSelf: 'stretch',
  } as any,
  metaPill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: 'rgba(15,23,42,0.04)',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  metaPillText: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED },
  priceRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    alignSelf: 'stretch',
  } as any,
  price: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right' },
  duration: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED, textAlign: 'right' },
  summary: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    lineHeight: 17,
    color: TEXT_MUTED,
    textAlign: 'right',
    opacity: 0.9,
    alignSelf: 'stretch',
  },
  ctaRow: { marginTop: 4, alignItems: 'flex-end', width: '100%' },
  ctaButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: `${KURSI_PURPLE}33`,
    backgroundColor: 'transparent',
  } as any,
  ctaButtonVoucher: {
    backgroundColor: KURSI_PURPLE,
    borderColor: 'transparent',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 8px 22px rgba(176,38,255,0.22)' } as any) : {}),
  },
  ctaButtonText: { fontFamily: theme.fonts.bold, fontSize: 12, color: '#FFF' },
  ctaButtonTextOutline: { color: KURSI_PURPLE },
});

// ─── Main Component ───────────────────────────────────────────────────────────

interface CoursesCatalogProps {
  anchorRef?: React.RefObject<View>;
  onSchemaCoursesChange?: (courses: SchemaCourseLite[]) => void;

  // free-text search (manual typing)
  initialSearch?: string;

  // ✅ real topic filter from URL (?topic=...)
  initialTopic?: string;
}

export const CoursesCatalog: React.FC<CoursesCatalogProps> = ({
  anchorRef,
  onSchemaCoursesChange,
  initialSearch,
  initialTopic,
}) => {
  const { width: screenW } = useWindowDimensions();
  const router = useRouter();

  const H_PADDING = theme.spacing.md;
  const GAP = theme.spacing.sm;
  const columns = screenW < 360 ? 1 : 2;
  const available = Math.max(0, screenW - H_PADDING * 2 - GAP * (columns - 1));
  const cardWidth = Platform.OS === 'web' ? 290 : Math.floor(available / columns);

  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [displayedCourses, setDisplayedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [providerOptions, setProviderOptions] = useState<string[]>([]);
  const [levelOptions, setLevelOptions] = useState<string[]>([]);

  // free-text search input
  const [search, setSearch] = useState(initialSearch ?? '');

  // ✅ real topic filter state
  const [topicFilter, setTopicFilter] = useState(normalizeStr(initialTopic) || '');

  const [filters, setFilters] = useState<ActiveFilters>(DEFAULT_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);


  // keep in sync if URL changes
  useEffect(() => {
    const t = normalizeStr(initialTopic) || '';
    setTopicFilter(t);
  }, [initialTopic]);

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('processed_raw_courses')
          .select(
            'id,course_id,source_url_key,title,provider,topic,type,level,format,price,duration,language,summary,images,source_urls,voucher_text,has_voucher,audience,outcomes'
          )
          .is('deleted_at', null)
          .order('id', { ascending: false })
          .limit(1200);

        if (error || !data) {
          setCourses([]);
          return;
        }

        const normalized = (data as any[]).map(normalizeCourseRow);
        const deduped = dedupeCourses(normalized);

        setCourses(deduped);

        const types = Array.from(new Set(deduped.map((c) => c.type).filter(isValidValue))).sort() as string[];
        const providers = Array.from(new Set(deduped.map((c) => c.provider).filter(isValidValue)))
          .sort()
          .slice(0, 40) as string[];
        const levels = Array.from(new Set(deduped.map((c) => c.level).filter(isValidValue))).sort() as string[];

        setTypeOptions(types);
        setProviderOptions(providers);
        setLevelOptions(levels);
      } catch (e) {
        console.error('CoursesCatalog fetch error:', e);
        setCourses([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  // ── Filter + search ──────────────────────────────────────────────────────
  useEffect(() => {
    let result = courses;

    // ✅ topic filter FIRST (real field filtering)
    const tf = normalizeKey(topicFilter);
    if (tf) {
      const hasExact = result.some((c) => normalizeKey(c.topic) === tf);
      result = result.filter((c) => {
        const ct = normalizeKey(c.topic);
        return hasExact ? ct === tf : ct.includes(tf);
      });
    }

    // free-text search (manual)
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((c) => {
        const title = normalizeStr(c.title).toLowerCase();
        const provider = normalizeStr(c.provider).toLowerCase();
        const topic = normalizeStr(c.topic).toLowerCase();
        const summary = normalizeStr(c.summary).toLowerCase();
        return title.includes(q) || provider.includes(q) || topic.includes(q) || summary.includes(q);
      });
    }

    if (filters.type) result = result.filter((c) => normalizeStr(c.type) === normalizeStr(filters.type));
    if (filters.provider) result = result.filter((c) => normalizeStr(c.provider) === normalizeStr(filters.provider));
    if (filters.level) result = result.filter((c) => normalizeStr(c.level) === normalizeStr(filters.level));

    if (filters.has_voucher === true) result = result.filter((c) => !!c.has_voucher);
    if (filters.has_voucher === false) result = result.filter((c) => !c.has_voucher);

    setFilteredCourses(result);
    setPage(1);
  }, [courses, topicFilter, search, filters]);

  // ── Pagination ────────────────────────────────────────────────────────────
  useEffect(() => {
    const sliced = filteredCourses.slice(0, page * PAGE_SIZE);
    setDisplayedCourses(sliced);
    setHasMore(sliced.length < filteredCourses.length);
  }, [filteredCourses, page]);

  // ── Schema feed (web) ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!onSchemaCoursesChange) return;
    if (isLoading) return;

    const lite: SchemaCourseLite[] = (displayedCourses || []).slice(0, 50).map((c) => ({
      title: c.title,
      provider: c.provider,
      source_urls: c.source_urls,
    }));

    onSchemaCoursesChange(lite);
  }, [displayedCourses, isLoading, onSchemaCoursesChange]);

  const loadMore = useCallback(() => setPage((p) => p + 1), []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (topicFilter) n++; // ✅ include topic filter
    if (filters.type) n++;
    if (filters.provider) n++;
    if (filters.level) n++;
    if (filters.has_voucher !== null) n++;
    return n;
  }, [topicFilter, filters]);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearch('');
    setTopicFilter(''); // ✅ clear topic filter too
  }, []);

  const clearOnlyTopic = useCallback(() => setTopicFilter(''), []);

  // ── Course handlers ───────────────────────────────────────────────────────

  const handleCoursePress = useCallback(
    async (course: Course) => {
      if (Platform.OS !== 'web') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }

      trackButtonClick('catalog_course_click', '/', {
        course_id: course.course_id,
        has_voucher: course.has_voucher,
      });

      trackCourseClick({
        course_id: course.course_id,
        course_name: course.title,
        course_type: course.type,
        mentor_name: course.provider,
      });

      const safeCourseId = normalizeStr(course.course_id) || normalizeStr(course.source_url_key) || String(course.id);

      router.push({
        pathname: '/course/[courseId]',
        params: {
          courseId: safeCourseId,
          source: 'catalog',
        },
      } as any);
    },
    [router]
  );


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <View
      ref={anchorRef}
      style={styles.section}
      accessible={false}
      {...(Platform.OS === 'web' ? ({ id: 'courses-catalog' } as any) : {})}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionKickerRow}>
          <View style={styles.accentDot} />
          <Text style={styles.sectionKicker}>גלה קורסים</Text>
          <View style={styles.accentDot} />
        </View>

        <Text style={styles.sectionTitle} {...(Platform.OS === 'web' ? ({ role: 'heading', 'aria-level': 2 } as any) : {})}>
          קטלוג הקורסים המלא
        </Text>

        <Text style={styles.sectionSubtitle}>
          מעל {courses.length > 0 ? `${courses.length}+` : '1,000'} קורסים ממיטב המכללות וספקי הלמידה בישראל
        </Text>
      </View>

      {/* ✅ Topic filter pill (when coming from /fields) */}
      {!!topicFilter && (
        <View style={styles.topicPillRow}>
          <Pressable
            style={({ pressed }) => [styles.topicPill, pressed && { opacity: 0.9 }]}
            onPress={clearOnlyTopic}
            accessible
            accessibilityRole="button"
            accessibilityLabel={`מסונן לפי תחום: ${topicFilter}. לחץ כדי לבטל`}
          >
            <X size={14} color={KURSI_PURPLE} />
            <Text style={styles.topicPillText}>מסונן לפי תחום: {topicFilter}</Text>
          </Pressable>
        </View>
      )}

      <View style={styles.controlsBar}>
        <View style={styles.searchWrap}>
          <View style={[styles.searchIconWrap, { pointerEvents: 'none' }]}>
            <Search size={16} color={TEXT_MUTED} />
          </View>
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="חפש קורס, ספק, נושא..."
            placeholderTextColor={TEXT_MUTED}
            returnKeyType="search"
            accessible
            accessibilityLabel="חיפוש קורסים"
            accessibilityHint="הקלד לחיפוש בקטלוג"
            {...(Platform.OS === 'web' ? ({ dir: 'rtl', style: [styles.searchInput, { outlineStyle: 'none' }] } as any) : {})}
          />
          {!!search && (
            <Pressable
              style={styles.clearSearch}
              onPress={() => setSearch('')}
              accessible
              accessibilityRole="button"
              accessibilityLabel="נקה חיפוש"
            >
              <X size={14} color={TEXT_MUTED} />
            </Pressable>
          )}
        </View>

        <Pressable
          style={[styles.filterButton, activeFilterCount > 0 && styles.filterButtonActive]}
          onPress={() => setIsFilterOpen((v) => !v)}
          accessible
          accessibilityRole="button"
          accessibilityLabel={`פילטרים${activeFilterCount > 0 ? ` (${activeFilterCount} פעילים)` : ''}`}
        >
          <Filter size={15} color={activeFilterCount > 0 ? KURSI_PURPLE : TEXT_MUTED} />
          <Text style={[styles.filterButtonText, activeFilterCount > 0 && styles.filterButtonTextActive]}>
            פילטר{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Text>
          <ChevronDown
            size={13}
            color={activeFilterCount > 0 ? KURSI_PURPLE : TEXT_MUTED}
            style={{ transform: [{ rotate: isFilterOpen ? '180deg' : '0deg' }] }}
          />
        </Pressable>
      </View>

      {isFilterOpen && (
        <View style={styles.filterPanel}>
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>הטבות</Text>
            <View style={styles.filterChips}>
              <FilterChip
                label="🎁 רק עם הטבה"
                active={filters.has_voucher === true}
                onPress={() => setFilters((f) => ({ ...f, has_voucher: f.has_voucher === true ? null : true }))}
              />
              <FilterChip
                label="בלי הטבה"
                active={filters.has_voucher === false}
                onPress={() => setFilters((f) => ({ ...f, has_voucher: f.has_voucher === false ? null : false }))}
              />
            </View>
          </View>

          {typeOptions.length > 0 && (
            <View style={styles.filterGroup}>
              <Text style={styles.filterGroupLabel}>סוג קורס</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterChips}>
                {typeOptions.map((t) => (
                  <FilterChip
                    key={t}
                    label={t}
                    active={filters.type === t}
                    onPress={() => setFilters((f) => ({ ...f, type: f.type === t ? '' : t }))}
                  />
                ))}
              </ScrollView>
            </View>
          )}

          {levelOptions.length > 0 && (
            <View style={styles.filterGroup}>
              <Text style={styles.filterGroupLabel}>רמה</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterChips}>
                {levelOptions.map((l) => (
                  <FilterChip
                    key={l}
                    label={l}
                    active={filters.level === l}
                    onPress={() => setFilters((f) => ({ ...f, level: f.level === l ? '' : l }))}
                  />
                ))}
              </ScrollView>
            </View>
          )}

          {providerOptions.length > 0 && (
            <View style={styles.filterGroup}>
              <Text style={styles.filterGroupLabel}>ספק / מכללה</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterChips}>
                {providerOptions.map((p) => (
                  <FilterChip
                    key={p}
                    label={p}
                    active={filters.provider === p}
                    onPress={() => setFilters((f) => ({ ...f, provider: f.provider === p ? '' : p }))}
                  />
                ))}
              </ScrollView>
            </View>
          )}

          {(activeFilterCount > 0 || search) && (
            <Pressable style={styles.clearAllButton} onPress={clearFilters}>
              <X size={13} color={KURSI_PURPLE} />
              <Text style={styles.clearAllText}>נקה הכל</Text>
            </Pressable>
          )}
        </View>
      )}

      {!isLoading && (
        <Text style={styles.resultsCount}>
          {filteredCourses.length === courses.length ? `${courses.length} קורסים` : `${filteredCourses.length} תוצאות`}
        </Text>
      )}

      {isLoading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator color={KURSI_PURPLE} size="large" />
          <Text style={styles.loadingText}>טוען קורסים...</Text>
        </View>
      ) : filteredCourses.length === 0 ? (
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyIcon}>🔍</Text>
          <Text style={styles.emptyTitle}>לא נמצאו קורסים</Text>
          <Text style={styles.emptySubtitle}>נסו לשנות את החיפוש או לנקות פילטרים</Text>
          <Pressable style={styles.clearAllButton} onPress={clearFilters}>
            <Text style={styles.clearAllText}>נקה פילטרים</Text>
          </Pressable>
        </View>
      ) : (
        <>
          <View style={styles.grid}>
            {displayedCourses.map((course) => (
              <CourseCard
                key={`${course.id}`}
                course={course}
                onPress={handleCoursePress}
                cardWidth={cardWidth}
              />
            ))}
          </View>

          {hasMore && (
            <Pressable
              style={({ pressed }) => [styles.loadMoreButton, pressed && styles.loadMoreButtonPressed]}
              onPress={loadMore}
              accessible
              accessibilityRole="button"
              accessibilityLabel={`טען עוד קורסים (${filteredCourses.length - displayedCourses.length} נוספים)`}
            >
              <Text style={styles.loadMoreText}>טען עוד ({filteredCourses.length - displayedCourses.length} נוספים)</Text>
              <ChevronDown size={16} color={KURSI_PURPLE} />
            </Pressable>
          )}
        </>
      )}

    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  section: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 920 : undefined,
    alignSelf: 'center',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: Platform.OS === 'web' ? 0 : theme.spacing.md,
  } as any,
  sectionHeader: { alignItems: 'center', marginBottom: theme.spacing.lg },
  sectionKickerRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8, marginBottom: 10 } as any,
  accentDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: KURSI_PURPLE, opacity: 0.7 },
  sectionKicker: { fontFamily: theme.fonts.bold, fontSize: 12, letterSpacing: 0.6, color: KURSI_PURPLE, textTransform: 'uppercase' } as any,
  sectionTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: Platform.OS === 'web' ? 38 : 28,
    lineHeight: Platform.OS === 'web' ? 46 : 36,
    color: TEXT,
    textAlign: 'center',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT_MUTED,
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: theme.spacing.md,
  },

  // ✅ topic pill
  topicPillRow: { marginBottom: theme.spacing.sm, alignItems: 'flex-end' },
  topicPill: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: `${KURSI_PURPLE}33`,
    backgroundColor: `${KURSI_PURPLE}0B`,
  } as any,
  topicPillText: {
    fontFamily: theme.fonts.bold,
    fontSize: 12,
    color: KURSI_PURPLE,
    textAlign: 'right',
  },

  controlsBar: { flexDirection: 'row-reverse', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.sm } as any,
  searchWrap: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: BG_CARD,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 12,
    height: 46,
    gap: 8,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 4px 16px rgba(15,23,42,0.07)' } as any) : {}),
  } as any,
  searchIconWrap: { opacity: 0.6 },
  searchInput: {
    flex: 1,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT,
    textAlign: 'right',
    height: 46,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  } as any,
  clearSearch: { padding: 4, opacity: 0.6 },
  filterButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    height: 46,
    backgroundColor: BG_CARD,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 4px 16px rgba(15,23,42,0.07)' } as any) : {}),
  } as any,
  filterButtonActive: { borderColor: KURSI_PURPLE, backgroundColor: `${KURSI_PURPLE}10` },
  filterButtonText: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT_MUTED },
  filterButtonTextActive: { color: KURSI_PURPLE },
  filterPanel: {
    backgroundColor: BG_CARD,
    borderRadius: theme.borderRadius.xl,
    borderWidth: 1,
    borderColor: BORDER,
    padding: 16,
    marginBottom: theme.spacing.md,
    gap: 14,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 8px 28px rgba(15,23,42,0.09)' } as any) : {}),
  } as any,
  filterGroup: { gap: 8 },
  filterGroupLabel: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_MUTED, textAlign: 'right', letterSpacing: 0.3 },
  filterChips: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8, paddingHorizontal: 2 } as any,
  clearAllButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: `${KURSI_PURPLE}33`,
    backgroundColor: `${KURSI_PURPLE}08`,
  } as any,
  clearAllText: { fontFamily: theme.fonts.bold, fontSize: 12, color: KURSI_PURPLE },
  resultsCount: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MUTED, textAlign: 'right', marginBottom: 10, paddingHorizontal: 2 },
  grid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? theme.spacing.lg : theme.spacing.sm,
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
  } as any,
  loadingWrap: { paddingVertical: 48, alignItems: 'center', gap: 14 },
  loadingText: { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MUTED },
  emptyWrap: { paddingVertical: 48, alignItems: 'center', gap: 10 },
  emptyIcon: { fontSize: 40 },
  emptyTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'center' },
  emptySubtitle: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MUTED, textAlign: 'center' },
  loadMoreButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: theme.spacing.lg,
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: `${KURSI_PURPLE}33`,
    backgroundColor: BG_CARD,
    alignSelf: 'center',
    minWidth: 200,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 8px 22px rgba(176,38,255,0.10)' } as any) : {}),
  } as any,
  loadMoreButtonPressed: { transform: [{ scale: 0.985 }], opacity: 0.9 },
  loadMoreText: { fontFamily: theme.fonts.bold, fontSize: 14, color: KURSI_PURPLE },
});

